import * as React from 'react';
import { GridColumn, GridToolbar } from '@progress/kendo-react-grid';
import styled from 'styled-components';
import { useEffect } from 'react';
import { Paragraph, PromptButtonsWrapper } from '../../../Styles';
import useAsyncAction, { useWindowDimensions } from '../../../../commonComponents/Hooks';
import Prompt from '../../../common/Prompt';
import SecondaryButton from '../../../../commonComponents/SecondaryButton';
import PrimaryButton from '../../../../commonComponents/PrimaryButton';
import ColumnInterface from '../../../../models/ColumnInterface';
import Entity, { createDefaultEntity } from '../../../../models/Entity';
import { getClientEntities, deleteClientEntity } from '../../../../core/api/publicClientApi';
import AddNewGridButton from '../../../../commonComponents/AddNewGridButton';
import { errorRed, isPhoneScreen, twoIconsCellWidth } from '../../../cssConstants';
import { updateVisibleColumns } from '../../../../core/domain';
import MobileSummaryCell from '../../../../commonComponents/gridCells/MobileSummaryCell';
import EditDeleteCommandCell from '../../../../commonComponents/gridCells/EditDeleteCommandCell';
import PublicFormButtons from '../common/PublicFormButtons';
import { PublicStyledGrid, StyledGridContainer, SubTitleParagraph, TitleParagraph } from '../common/PublicLayout';
import PublicBusinessEntityForm from './PublicBusinessEntityForm';
import { useProgress } from '../common/PublicProgressContext';

const PublicBusinessEntitiesGrid = (props: Props) => {
	const { onBackButtonClick, onContinueButtonClick } = props;
	const [columns, setColumns] = React.useState(initialColumns);
	const [entities, setEntities] = React.useState<Entity[]>([]);
	const { width: screenWidth } = useWindowDimensions();

	const [openForm, setOpenForm] = React.useState(false);
	const [editItem, setEditItem] = React.useState<Entity>();
	const [deleteItem, setDeleteItem] = React.useState<Entity>();
	const [hasValues, setHasValues] = React.useState(false);
	const [gridErrorMessage, setGridErrorMessage] = React.useState('');

	const performAsyncAction = useAsyncAction();
	const { signal } = new AbortController();
	const { clientBasicInfoCompleted } = useProgress();

	React.useEffect(() => {
		if (clientBasicInfoCompleted) {
			performAsyncAction(async () => {
				await loadData();
			});
		}
	}, []);

	React.useEffect(() => {
		updateVisibleColumns(initialColumns, columns, screenWidth, setColumns);
	}, [screenWidth]);

	const loadData = async () => {
		const existingEntities = await getClientEntities(signal);
		setEntities(existingEntities);
	};

	const handleAddNew = () => {
		setEditItem({ ...createDefaultEntity(), isTradingCompany: false });
		setOpenForm(true);
	};

	const handleEdit = (item: Entity) => {
		setEditItem(createDefaultEntity(entities.find((x) => x.entityId === item.entityId)));
		setOpenForm(true);
	};

	const handleCancelEdit = () => {
		setOpenForm(false);
	};

	const handleSubmit = async () => {
		performAsyncAction(async () => {
			await loadData();
		});
		setOpenForm(false);
	};

	const openConfirmdeleteDialog = (item: Entity) => {
		setDeleteItem(item);
	};

	const closeConfirmDeleteDialog = () => {
		setDeleteItem(null);
	};

	const handleDelete = async () => {
		await performAsyncAction(async () => {
			await deleteClientEntity(deleteItem.entityId, signal);
			closeConfirmDeleteDialog();
		});
		const newData = entities.filter((item) => deleteItem.entityId !== item.entityId);
		setEntities(newData);
	};

	useEffect(() => {
		setHasValues(entities.length > 0);
		setGridErrorMessage('');
	}, [entities]);

	const handleContinueButtonClick = () => {
		if (hasValues) {
			onContinueButtonClick();
		} else {
			setGridErrorMessage('At least one entity is required');
		}
	};

	return (
		<>
			{!openForm && (
				<>
					<TitleParagraph>Business Entities</TitleParagraph>
					<SubTitleParagraph>Please provide the details of your business entities.</SubTitleParagraph>
					<StyledGridContainer>
						<StyledEntityGrid data={entities} key={columns.length}>
							{columns.map((column) => {
								// eslint-disable-next-line react/jsx-props-no-spreading
								return <GridColumn {...column} key={column.field} />;
							})}
							{isPhoneScreen(screenWidth) && <GridColumn cells={{ data: MobileSummaryCell(initialColumns) }} />}
							<GridColumn
								cells={{ data: EditDeleteCommandCell(handleEdit, openConfirmdeleteDialog) }}
								width={twoIconsCellWidth(screenWidth)}
							/>
						</StyledEntityGrid>
						<GridToolbar>
							<AddNewGridButton fullName="Add a business entity" onClick={handleAddNew} />
						</GridToolbar>
					</StyledGridContainer>
					{deleteItem && (
						<Prompt
							title="Delete Entity"
							content={<Paragraph>{`Do you want to delete this entity: ${deleteItem.name}?`}</Paragraph>}
							onClose={closeConfirmDeleteDialog}
							actions={
								<PromptButtonsWrapper>
									<SecondaryButton text="Cancel" onClick={closeConfirmDeleteDialog} />
									<PrimaryButton text="Delete" fullWidth={false} onClick={async () => handleDelete()} />
								</PromptButtonsWrapper>
							}
						/>
					)}
					<GridError>{gridErrorMessage}</GridError>
					<PublicFormButtons onBackButtonClick={onBackButtonClick} onContinueButtonClick={handleContinueButtonClick} />
				</>
			)}
			{openForm && (
				<>
					<TitleParagraph>Add a Business Entity</TitleParagraph>
					<SubTitleParagraph>Please provide the details of your business entity.</SubTitleParagraph>
					<PublicBusinessEntityForm onSubmit={handleSubmit} onCancel={handleCancelEdit} entity={editItem} />
				</>
			)}
		</>
	);
};

export default PublicBusinessEntitiesGrid;

interface Props {
	onBackButtonClick?: () => void;
	onContinueButtonClick?: () => void;
}

const initialColumns: ColumnInterface[] = [
	{ field: 'name', title: 'Entity name' },
	{ field: 'type', title: 'Entity type' }
];

const StyledEntityGrid = styled(PublicStyledGrid)`
	tr {
		cursor: pointer;
	}
`;

const GridError = styled.p`
	font-size: 14px;
	line-height: 16px;
	color: ${errorRed};
	margin: 10px 0 0 8px;
	text-align: center;
`;
